import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import { Component, Watch } from "@/decorators";
import UsuarioModel from "@/models/usuarioModel";
import { AppState, Getters, SessionActions } from "@/store/store";
import Bus from "@/utils/events/bus";

import loadMenus, { MenuItem } from "./navmenuitens";

import "./navmenu.scss";

// Mesmo corte do CSS (@media max-width: 767px), da home e de src/react/shared/hooks/useIsMobile.ts.
// Usa matchMedia em vez de window.innerWidth: em telas não responsivas o navegador do celular
// alarga a viewport (innerWidth passa de 900) e o innerWidth deixaria de bater com o CSS.
const MOBILE_QUERY = "(max-width: 767px)";

@Component({
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(
            ["VALIDAR_PERMISSAO_USUARIO", "GET_IS_ESTEREIS", "GET_FRANQUIA_IS_ESTEREIS", "GET_IS_FRACIONAMENTO"] as Getters,
        ),
    },
})
export default class NavMenuComponent extends Vue {
    // State computed props
    VALIDAR_PERMISSAO_USUARIO: (telaDescricao: string, acaoController: string) => boolean;
    GET_IS_ESTEREIS: () => Promise<boolean>;
    GET_FRANQUIA_IS_ESTEREIS: () => Promise<boolean>;
    GET_IS_FRACIONAMENTO: () => Promise<boolean>;
    usuarioLogado: UsuarioModel;

    isVisible = false;
    private isMobileViewport = false;
    selectedMainMenuItem = "";
    selectedSubMenuItem = "";
    mainMenuItens: Array<MenuItem> = [];
    groupedSubMenuItens: any = {};

    private showMenuLevelOneItens(menu: string) {
        if (this.selectedMainMenuItem == menu) {
            this.selectedMainMenuItem = "";
        } else {
            this.selectedMainMenuItem = menu;
        }
    }

    private showGroupedMenuItens(item) {
        this.groupedSubMenuItens[item] = !this.groupedSubMenuItens[item];
        this.$forceUpdate();
    }

    private selectMenu(item: string) {
        this.selectedSubMenuItem = item;

        // No celular, acessar uma tela recolhe o menu (inclusive ao tocar na tela atual, quando
        // a rota não muda e o watcher de $route não dispara).
        if (this.matchesMobile()) {
            this.isVisible = false;
        }
    }

    private matchesMobile() {
        return window.matchMedia(MOBILE_QUERY).matches;
    }

    // No celular o menu começa fechado e só abre pelo hambúrguer da barra superior.
    private handleResize() {
        const isMobile = this.matchesMobile();
        const crossedBreakpoint = isMobile !== this.isMobileViewport;
        this.isMobileViewport = isMobile;

        // No celular, um resize que não cruza o breakpoint (barra de endereço, teclado) não
        // fecha o menu que o usuário abriu.
        if (isMobile && !crossedBreakpoint) {
            return;
        }

        this.isVisible = !isMobile;
    }

    @Watch("$route")
    private function() {
        if (this.$route.path == "/") {
            this.selectedMainMenuItem = "";
        }

        if (this.matchesMobile()) {
            this.isVisible = false;
        }
    }

    private created() {
        this.handleResize = this.handleResize.bind(this);
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    }

    @Watch("isVisible")
    private onVisibleChanged() {
        Bus.$emit("nav-menu-change", this.isVisible);
    }

    private async beforeMount() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO, true);

        const isEstereisOuFracionamento = (await this.GET_IS_FRACIONAMENTO()) || (await this.GET_IS_ESTEREIS());
        const bloquearCadastroDeProduto =
            isEstereisOuFracionamento && !this.VALIDAR_PERMISSAO_USUARIO("produtos", "InsertProdutoIndustria");

        this.mainMenuItens = await loadMenus({
            VALIDAR_PERMISSAO_USUARIO: this.VALIDAR_PERMISSAO_USUARIO,
            franquiaId: this.usuarioLogado.franquiaId,
            isEstereis: await this.GET_FRANQUIA_IS_ESTEREIS(),
            isEstereisOuFracionamento: isEstereisOuFracionamento,
            bloquearCadastroDeProduto: bloquearCadastroDeProduto,
        });

        for (const menuItem of this.mainMenuItens) {
            for (const subMenuItem of menuItem.itens) {
                this.groupedSubMenuItens[subMenuItem.name] = false;
            }
        }
    }

    private mounted() {
        Bus.$on("hide-nav-menu", (force = false) => {
            if (force || this.matchesMobile()) {
                this.isVisible = false;
            }
        });

        Bus.$on("show-nav-menu", (force = false) => {
            if (force || !this.matchesMobile()) {
                this.isVisible = true;
            }
        });

        Bus.$on("toggle-nav-menu", () => {
            this.isVisible = !this.isVisible;
        });
    }

    private beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    }
}
